import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAdminStats, useDailyStats, useDailyUserStats, useRevenueStats, type Period } from '../api/admin-api'
import { DailyStatsChart } from './DailyStatsChart'
import { DailyStatsTable } from './DailyStatsTable'
import { DailyRevenueChart } from './DailyRevenueChart'
import { DailyRevenueTable } from './DailyRevenueTable'
import { DailyUserStatsChart } from './DailyUserStatsChart'
import { HourlyStatsModal } from './HourlyStatsModal'
import { PeriodSelector } from './PeriodSelector'
import {
  Palette,
  LayoutTemplate,
  FolderOpen,
  ImageIcon,
  CalendarDays,
  CalendarRange,
  Users,
  ShieldCheck,
  TrendingUp,
  BarChart3,
  Zap,
  UserPlus,
  DollarSign,
  Wallet,
} from 'lucide-react'

function formatNumber(num: number | string): string {
  const n = typeof num === 'string' ? parseInt(num, 10) : num
  if (isNaN(n)) return String(num)
  return n.toLocaleString('ko-KR')
}

function formatPeakDate(dateStr: string): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  const dayNames = ['일', '월', '화', '수', '목', '금', '토']
  return `${date.getMonth() + 1}/${date.getDate()} (${dayNames[date.getDay()]})`
}

interface StatCardProps {
  icon: React.ReactNode
  iconBg: string
  title: string
  value: string | number
  unit?: string
  subtitle?: React.ReactNode
  to?: string
}

function StatCard({ icon, iconBg, title, value, unit = '', subtitle, to }: StatCardProps) {
  const content = (
    <div className="flex items-start gap-4">
      <div className={`p-3 rounded-lg ${iconBg}`}>{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">
          {formatNumber(value)}
          {unit && <span className="text-lg font-medium text-gray-500 ml-0.5">{unit}</span>}
        </p>
        {subtitle && <div className="mt-2">{subtitle}</div>}
      </div>
    </div>
  )

  if (to) {
    return (
      <Link
        to={to}
        className="block bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md hover:border-gray-200 transition-all duration-200"
      >
        {content}
      </Link>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md hover:border-gray-200 transition-all duration-200">
      {content}
    </div>
  )
}

interface StatusBadgeProps {
  enabled: number
  disabled: number
}

function StatusBadge({ enabled, disabled }: StatusBadgeProps) {
  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        <span className="text-gray-600">활성 {formatNumber(enabled)}</span>
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
        <span className="text-gray-400">비활성 {formatNumber(disabled)}</span>
      </span>
    </div>
  )
}

interface MiniStatProps {
  icon: React.ReactNode
  label: string
  value: string | number
  unit?: string
  subtext?: string
  highlight?: boolean
}

function MiniStat({ icon, label, value, unit = '', subtext, highlight }: MiniStatProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-100 p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-gray-400">{icon}</span>
        <span className="text-xs font-medium text-gray-500">{label}</span>
      </div>
      <p className={`text-xl font-bold ${highlight ? 'text-emerald-600' : 'text-gray-900'}`}>
        {formatNumber(value)}
        {unit && <span className="text-sm font-medium text-gray-400 ml-0.5">{unit}</span>}
      </p>
      {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
    </div>
  )
}

export function Dashboard() {
  const { data: stats, isLoading, error } = useAdminStats()
  const [period, setPeriod] = useState<Period>({ type: 'preset', days: 14 })
  const [userPeriod, setUserPeriod] = useState<Period>({ type: 'preset', days: 14 })
  const [revenuePeriod, setRevenuePeriod] = useState<Period>({ type: 'preset', days: 14 })
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const { data: dailyStats, isLoading: isDailyLoading } = useDailyStats(period)
  const { data: revenueStats, isLoading: isRevenueLoading } = useRevenueStats(revenuePeriod)
  const { data: dailyUserStats, isLoading: isDailyUserLoading } = useDailyUserStats(userPeriod)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">통계를 불러올 수 없습니다: {error.message}</div>
      </div>
    )
  }

  if (!stats) return null

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-heading tracking-tight">대시보드</h1>
        <p className="text-caption mt-2 leading-relaxed">픽스젠 관리자 패널</p>
      </div>

      {/* 리소스 통계 */}
      <section>
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
          리소스
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            icon={<Palette className="w-5 h-5 text-violet-600" />}
            iconBg="bg-violet-50"
            title="스타일"
            value={stats.styles.total}
            unit="개"
            to="/admin/styles"
            subtitle={
              <StatusBadge enabled={stats.styles.enabled} disabled={stats.styles.disabled} />
            }
          />
          <StatCard
            icon={<LayoutTemplate className="w-5 h-5 text-blue-600" />}
            iconBg="bg-blue-50"
            title="템플릿"
            value={stats.templates.total}
            unit="개"
            to="/admin/templates"
            subtitle={
              <StatusBadge
                enabled={stats.templates.enabled}
                disabled={stats.templates.disabled}
              />
            }
          />
          <StatCard
            icon={<FolderOpen className="w-5 h-5 text-amber-600" />}
            iconBg="bg-amber-50"
            title="템플릿 카테고리"
            value={stats.categories.total}
            unit="개"
            to="/admin/categories"
            subtitle={
              <StatusBadge
                enabled={stats.categories.enabled}
                disabled={stats.categories.disabled}
              />
            }
          />
        </div>
      </section>

      {/* 생성 통계 */}
      <section>
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
          이미지 생성
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            icon={<ImageIcon className="w-5 h-5 text-emerald-600" />}
            iconBg="bg-emerald-50"
            title="오늘"
            value={stats.generations.today}
            unit="건"
            to="/admin/generations"
          />
          <StatCard
            icon={<CalendarDays className="w-5 h-5 text-cyan-600" />}
            iconBg="bg-cyan-50"
            title="이번 주"
            value={stats.generations.thisWeek}
            unit="건"
            to="/admin/generations"
          />
          <StatCard
            icon={<CalendarRange className="w-5 h-5 text-indigo-600" />}
            iconBg="bg-indigo-50"
            title="이번 달"
            value={stats.generations.thisMonth}
            unit="건"
            to="/admin/generations"
          />
        </div>
      </section>

      {/* 일별 생성 추이 */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            일별 생성 추이
          </h2>
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>

        {isDailyLoading ? (
          <div className="h-64 flex items-center justify-center text-gray-500 bg-white rounded-lg border">
            로딩 중...
          </div>
        ) : dailyStats ? (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniStat
                icon={<BarChart3 className="w-4 h-4" />}
                label="기간 합계"
                value={dailyStats.summary.totalImages}
                unit="건"
              />
              <MiniStat
                icon={<CalendarDays className="w-4 h-4" />}
                label="일평균"
                value={dailyStats.summary.averagePerDay}
                unit="건"
              />
              <MiniStat
                icon={<Zap className="w-4 h-4" />}
                label="최고 일자"
                value={dailyStats.summary.peakDay.count}
                unit="건"
                subtext={formatPeakDate(dailyStats.summary.peakDay.date)}
                highlight
              />
              <MiniStat
                icon={<ImageIcon className="w-4 h-4" />}
                label="생성 요청"
                value={dailyStats.summary.totalGenerations}
                unit="회"
              />
            </div>

            {/* Chart */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <DailyStatsChart data={dailyStats.dailyStats} onBarClick={setSelectedDate} />
            </div>

            {/* Table */}
            <DailyStatsTable data={dailyStats.dailyStats} />
          </div>
        ) : null}
      </section>

      {/* 매출 */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              매출
            </h2>
            <p className="text-xs text-gray-400 mt-1">매출 = 생성 이미지 × 500원</p>
          </div>
          <PeriodSelector value={revenuePeriod} onChange={setRevenuePeriod} />
        </div>

        {isRevenueLoading ? (
          <div className="h-64 flex items-center justify-center text-gray-500 bg-white rounded-lg border">
            로딩 중...
          </div>
        ) : revenueStats ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniStat
                icon={<DollarSign className="w-4 h-4" />}
                label="총 매출"
                value={revenueStats.summary.totalRevenueKrw}
                unit="원"
              />
              <MiniStat
                icon={<Wallet className="w-4 h-4" />}
                label="총 원가"
                value={revenueStats.summary.totalCostKrw}
                unit="원"
              />
              <MiniStat
                icon={<TrendingUp className="w-4 h-4" />}
                label="총 차익"
                value={revenueStats.summary.totalProfitKrw}
                unit="원"
                subtext={`마진 ${revenueStats.summary.marginPercent}%`}
                highlight
              />
              <MiniStat
                icon={<CalendarDays className="w-4 h-4" />}
                label="일평균 매출"
                value={revenueStats.summary.averageRevenuePerDay}
                unit="원"
              />
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <DailyRevenueChart data={revenueStats.dailyStats} />
            </div>

            <DailyRevenueTable data={revenueStats.dailyStats} />
          </div>
        ) : null}
      </section>

      {/* 사용자 통계 */}
      <section>
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
          사용자
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            icon={<Users className="w-5 h-5 text-rose-600" />}
            iconBg="bg-rose-50"
            title="전체 사용자"
            value={stats.users.total}
            unit="명"
            subtitle={
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>관리자 {formatNumber(stats.users.admins)}명</span>
              </div>
            }
          />
        </div>
      </section>

      {/* 일별 사용자 가입 추이 */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            일별 가입 추이
          </h2>
          <PeriodSelector value={userPeriod} onChange={setUserPeriod} />
        </div>

        {isDailyUserLoading ? (
          <div className="h-64 flex items-center justify-center text-gray-500 bg-white rounded-lg border">
            로딩 중...
          </div>
        ) : dailyUserStats ? (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <MiniStat
                icon={<BarChart3 className="w-4 h-4" />}
                label="기간 합계"
                value={dailyUserStats.summary.totalNewUsers}
                unit="명"
              />
              <MiniStat
                icon={<CalendarDays className="w-4 h-4" />}
                label="일평균"
                value={dailyUserStats.summary.averagePerDay}
                unit="명"
              />
              <MiniStat
                icon={<Zap className="w-4 h-4" />}
                label="최고 일자"
                value={dailyUserStats.summary.peakDay.count}
                unit="명"
                subtext={formatPeakDate(dailyUserStats.summary.peakDay.date)}
                highlight
              />
            </div>

            {/* Chart */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <DailyUserStatsChart data={dailyUserStats.dailyStats} />
            </div>
          </div>
        ) : null}
      </section>

      {/* Hourly Stats Modal */}
      <HourlyStatsModal date={selectedDate} onClose={() => setSelectedDate(null)} />
    </div>
  )
}
